import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Badge, Button, Spinner, Alert, Table, Modal, ListGroup } from 'react-bootstrap';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnOrderState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { useCaseTransactions, defaultFilters, SourceType, UnifiedRow, ExportProgress } from '../../hooks/useCaseTransactions';
import { formatCurrency } from '../../utils/formatters';
import {
  fetchCustomColumnDefinitions,
  createCustomColumnDefinition,
  updateCustomColumnDefinition,
  deleteCustomColumnDefinition,
  submitCustomColumnClassification,
  fetchCustomColumnValues,
  CustomColumnDefinition,
  CustomColumnChoice,
  ClassifyRecord,
} from '../../api/cases';

interface DataExportProps {
  case_id: string;
}

const SOURCE_LABELS: Record<SourceType, string> = {
  bank_statement: 'Bank Statements',
  credit_card: 'Credit Card',
  check_image: 'Check Images',
  general_ledger: 'General Ledger',
  expense_report: 'Expense Reports',
};

// ── Column model ─────────────────────────────────────────────────────────────
// Three kinds of column can appear in the export:
//   'field'          - a value already on the row (built in, can't be removed,
//                       only shown/hidden and reordered)
//   'absolute_value'  - a user-added column: |value| of another numeric field
//   'classifier'      - a user-added column: LLM-classified value against a
//                       saved, reusable definition (name + option list),
//                       cached server-side per record_id

type ColumnKind = 'field' | 'absolute_value' | 'classifier';

interface ExportColumn {
  id: string;
  label: string;
  kind: ColumnKind;
  sourceKey?: keyof UnifiedRow;   // for 'field' and 'absolute_value'
  definitionId?: string;          // for 'classifier'
  format?: (row: UnifiedRow) => string;
  removable: boolean;
}

// Fields a classifier column can be given as context, and a field/absolute
// value column can be based on. Kept separate from the display column list
// since context selection should offer raw fields, not formatted labels.
const CONTEXT_FIELD_OPTIONS: { key: string; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'description', label: 'Description' },
  { key: 'amount', label: 'Amount' },
  { key: 'source_type', label: 'Source' },
  { key: 'institution', label: 'Institution' },
  { key: 'account_last4', label: 'Account (last 4)' },
  { key: 'transaction_type', label: 'Type (raw: credit/debit/etc.)' },
  { key: 'payee', label: 'Payee (checks only)' },
  { key: 'gl_account_name', label: 'GL Account Name' },
];

const NUMERIC_FIELDS: { key: keyof UnifiedRow; label: string }[] = [
  { key: 'amount', label: 'Amount' },
  { key: 'debit_amount', label: 'Debit' },
  { key: 'credit_amount', label: 'Credit' },
];

const BUILT_IN_FIELDS: ExportColumn[] = [
  { id: 'date', label: 'Date', kind: 'field', sourceKey: 'date', removable: false },
  { id: 'description', label: 'Description', kind: 'field', sourceKey: 'description', removable: false },
  { id: 'amount', label: 'Amount', kind: 'field', sourceKey: 'amount', removable: false,
    format: (r) => (r.amount != null ? formatCurrency(r.amount) : '') },
  { id: 'source_type', label: 'Source', kind: 'field', sourceKey: 'source_type', removable: false,
    format: (r) => SOURCE_LABELS[r.source_type] },
  { id: 'institution', label: 'Institution', kind: 'field', sourceKey: 'institution', removable: false },
  { id: 'account_number', label: 'Account #', kind: 'field', sourceKey: 'account_number', removable: false },
  { id: 'account_last4', label: 'Account (last 4)', kind: 'field', sourceKey: 'account_last4', removable: false },
  { id: 'transaction_type', label: 'Type', kind: 'field', sourceKey: 'transaction_type', removable: false },
  { id: 'debit_amount', label: 'Debit', kind: 'field', sourceKey: 'debit_amount', removable: false,
    format: (r) => (r.debit_amount != null ? formatCurrency(r.debit_amount) : '') },
  { id: 'credit_amount', label: 'Credit', kind: 'field', sourceKey: 'credit_amount', removable: false,
    format: (r) => (r.credit_amount != null ? formatCurrency(r.credit_amount) : '') },
  { id: 'gl_account_code', label: 'GL Account Code', kind: 'field', sourceKey: 'gl_account_code', removable: false },
  { id: 'gl_account_name', label: 'GL Account Name', kind: 'field', sourceKey: 'gl_account_name', removable: false },
  { id: 'check_number', label: 'Check #', kind: 'field', sourceKey: 'check_number', removable: false },
  { id: 'payee', label: 'Payee', kind: 'field', sourceKey: 'payee', removable: false },
  { id: 'file_name', label: 'Source File', kind: 'field', sourceKey: 'file_name', removable: false },
  { id: 'statement_date', label: 'Statement Date', kind: 'field', sourceKey: 'statement_date', removable: false },
];

const DEFAULT_VISIBLE_IDS = ['date', 'description', 'amount', 'source_type', 'institution', 'account_last4'];

// Batch size for classification calls - the accuracy ceiling found by ANNA
// (a production LLM transaction-categorization system): beyond ~100
// records in one classification call, the model starts hallucinating
// record ids. This is purely an accuracy consideration now, not a latency
// one - the actual LLM call runs server-side in an async worker Lambda
// (see classifyCustomColumnBatch.py), not synchronously behind the 29s
// API Gateway wall a naive version of this hit earlier.
const CLASSIFY_BATCH_SIZE = 100;

type ClassifierValues = Record<string, Record<string, string>>; // definitionId -> record_id -> value

function resolveValue(col: ExportColumn, row: UnifiedRow, classifierValues: ClassifierValues): string {
  if (col.kind === 'field') {
    if (col.format) return col.format(row);
    const v = col.sourceKey ? (row as any)[col.sourceKey] : undefined;
    return v ?? '';
  }
  if (col.kind === 'absolute_value') {
    const v = col.sourceKey ? (row as any)[col.sourceKey] : undefined;
    return typeof v === 'number' ? formatCurrency(Math.abs(v)) : '';
  }
  // classifier
  if (col.definitionId) {
    return classifierValues[col.definitionId]?.[row.record_id] ?? '';
  }
  return '';
}

function escapeCSV(value: unknown): string {
  if (value == null) return '';
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function triggerDownload(content: string, filename: string): void {
  // Excel doesn't reliably assume UTF-8 for a BOM-less CSV - without this,
  // non-ASCII characters (the em-dash in "Check — payee", accented names,
  // etc.) render as mangled replacement characters when opened in Excel.
  const blob = new Blob(['﻿' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

const columnHelper = createColumnHelper<UnifiedRow>();
let nextCustomColumnNum = 1;

const DataExport: React.FC<DataExportProps> = ({ case_id }) => {
  const auth = useAuth();
  const token = auth.user?.id_token;

  const [sourceTypes, setSourceTypes] = useState<Set<SourceType>>(defaultFilters().sourceTypes);
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filters = useMemo(() => ({
    sourceTypes,
    accounts: selectedAccounts,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [sourceTypes, selectedAccounts, dateFrom, dateTo]);

  const { rows, loading, error, availableAccounts, glTruncated, glTotalMatched, fetchAllRowsForExport } = useCaseTransactions(case_id, token, filters);

  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);

  const [allColumns, setAllColumns] = useState<ExportColumn[]>(BUILT_IN_FIELDS);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(BUILT_IN_FIELDS.map(c => c.id));
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const vis: VisibilityState = {};
    for (const col of BUILT_IN_FIELDS) vis[col.id] = DEFAULT_VISIBLE_IDS.includes(col.id);
    return vis;
  });
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [viewDefinitionId, setViewDefinitionId] = useState<string | null>(null);

  const [definitions, setDefinitions] = useState<CustomColumnDefinition[]>([]);
  const [classifierValues, setClassifierValues] = useState<ClassifierValues>({});
  const [classifying, setClassifying] = useState<string | null>(null); // definitionId currently running
  const [classifyProgress, setClassifyProgress] = useState<{ done: number; total: number } | null>(null);

  const dndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    fetchCustomColumnDefinitions(token)
      .then(({ definitions }) => setDefinitions(definitions))
      .catch(() => setDefinitions([]));
  }, [token]);

  // Selecting an account whose source type later gets unchecked would leave
  // a stale, no-longer-visible filter silently applied - drop it instead.
  useEffect(() => {
    const availableKeys = new Set(availableAccounts.map(a => a.key));
    setSelectedAccounts(prev => {
      const next = new Set(Array.from(prev).filter(k => availableKeys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [availableAccounts]);

  const columns = useMemo(() => allColumns.map(col =>
    columnHelper.accessor((row) => resolveValue(col, row, classifierValues), {
      id: col.id,
      header: col.label,
      cell: (ctx) => ctx.getValue(),
    })
  ), [allColumns, classifierValues]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnOrder, columnVisibility },
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  const toggleSourceType = (type: SourceType) => {
    setSourceTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  };

  const toggleAccount = (key: string) => {
    setSelectedAccounts(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleColumnVisible = (id: string) => {
    setColumnVisibility(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const removeColumn = (id: string) => {
    setAllColumns(prev => prev.filter(c => c.id !== id));
    setColumnOrder(prev => prev.filter(cid => cid !== id));
    setColumnVisibility(prev => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumnOrder(prev => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const addAbsoluteValueColumn = (sourceKey: keyof UnifiedRow, label: string) => {
    const id = `abs_${sourceKey}_${nextCustomColumnNum++}`;
    const newCol: ExportColumn = { id, label, kind: 'absolute_value', sourceKey, removable: true };
    setAllColumns(prev => [...prev, newCol]);
    setColumnOrder(prev => [...prev, id]);
    setColumnVisibility(prev => ({ ...prev, [id]: true }));
    setShowAddColumn(false);
  };

  const addClassifierColumn = (definition: CustomColumnDefinition) => {
    const id = `clf_${definition.definition_id}`;
    if (allColumns.some(c => c.id === id)) {
      setColumnVisibility(prev => ({ ...prev, [id]: true }));
      setShowAddColumn(false);
      return;
    }
    const newCol: ExportColumn = { id, label: definition.name, kind: 'classifier', definitionId: definition.definition_id, removable: true };
    setAllColumns(prev => [...prev, newCol]);
    setColumnOrder(prev => [...prev, id]);
    setColumnVisibility(prev => ({ ...prev, [id]: true }));
    setShowAddColumn(false);
  };

  // Classifies targetRows against a definition. Submits work in
  // CLASSIFY_BATCH_SIZE chunks (each submit call returns almost instantly -
  // it only checks the cache and kicks off background work for the rest,
  // it never runs the LLM call itself while the browser waits), then polls
  // for results until everything resolves or POLL_MAX_ATTEMPTS is hit.
  // Anything still unresolved after that is left blank rather than fabricated
  // or hung on indefinitely - consistent with how the backend already drops
  // any classification it isn't confident matches a real record.
  const POLL_INTERVAL_MS = 2500;
  const POLL_MAX_ATTEMPTS = 30; // ~75s ceiling

  const runClassification = async (
    definition: CustomColumnDefinition,
    targetRows: UnifiedRow[],
    onProgress?: (done: number, total: number) => void,
  ): Promise<Record<string, string>> => {
    const merged: Record<string, string> = { ...(classifierValues[definition.definition_id] ?? {}) };
    const toClassify = targetRows.filter(r => r.record_id && !(r.record_id in merged));
    if (toClassify.length === 0) return merged;

    const toRecord = (row: UnifiedRow): ClassifyRecord => ({
      record_id: row.record_id,
      record_type: row.source_type,
      file_id: row.file_id,
      context: Object.fromEntries(definition.context_columns.map(key => [key, (row as any)[key]])),
    });

    // Submit every chunk up front - each is a fast call, so there's no
    // reason to wait for one chunk's background work before submitting
    // the next.
    for (let i = 0; i < toClassify.length; i += CLASSIFY_BATCH_SIZE) {
      const batch = toClassify.slice(i, i + CLASSIFY_BATCH_SIZE).map(toRecord);
      const result = await submitCustomColumnClassification(case_id, definition.definition_id, batch, token);
      Object.assign(merged, result.values); // anything already cached comes back immediately
    }

    onProgress?.(Object.keys(merged).length - (targetRows.length - toClassify.length), toClassify.length);

    // Poll for the rest.
    let remaining = toClassify.filter(r => !(r.record_id in merged));
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS && remaining.length > 0; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      const { values } = await fetchCustomColumnValues(case_id, definition.definition_id, remaining.map(r => r.record_id), token);
      Object.assign(merged, values);
      remaining = remaining.filter(r => !(r.record_id in merged));
      onProgress?.(toClassify.length - remaining.length, toClassify.length);
    }

    setClassifierValues(prev => ({ ...prev, [definition.definition_id]: merged }));
    return merged;
  };

  const handleRunClassifierPreview = async (definitionId: string) => {
    const definition = definitions.find(d => d.definition_id === definitionId);
    if (!definition) return;
    setClassifying(definitionId);
    setClassifyProgress(null);
    try {
      await runClassification(definition, rows, (done, total) => setClassifyProgress({ done, total }));
    } catch (e: any) {
      // Leave already-classified rows in place; the column just shows blanks
      // for whatever didn't finish rather than losing progress.
    } finally {
      setClassifying(null);
      setClassifyProgress(null);
    }
  };

  const columnsById = useMemo(() => new Map(allColumns.map(c => [c.id, c])), [allColumns]);
  const orderedColumns = columnOrder.map(id => columnsById.get(id)).filter((c): c is ExportColumn => !!c);
  const visibleOrderedColumns = orderedColumns.filter(c => columnVisibility[c.id]);
  const activeClassifierColumns = visibleOrderedColumns.filter(c => c.kind === 'classifier' && c.definitionId);

  const handleExport = async () => {
    if (visibleOrderedColumns.length === 0 || rows.length === 0) return;
    setExporting(true);
    setExportProgress(null);
    try {
      // Always pulls the COMPLETE filtered set, not just what's loaded for
      // preview - GL alone can run into tens of thousands of entries past
      // the preview cap, so export pages through all of it independently.
      const exportRows = await fetchAllRowsForExport(setExportProgress);

      // Any active classifier column must be fully classified against the
      // COMPLETE export set, not just whatever was previewed - re-running
      // is cheap for already-cached rows (instant, no LLM call).
      const exportClassifierValues: ClassifierValues = { ...classifierValues };
      for (const col of activeClassifierColumns) {
        const definition = definitions.find(d => d.definition_id === col.definitionId);
        if (!definition) continue;
        setClassifying(definition.definition_id);
        const merged = await runClassification(definition, exportRows, (done, total) => setClassifyProgress({ done, total }));
        exportClassifierValues[definition.definition_id] = merged;
      }
      setClassifying(null);
      setClassifyProgress(null);

      const header = visibleOrderedColumns.map(c => c.label).join(',');
      const lines = exportRows.map(row =>
        visibleOrderedColumns.map(col => escapeCSV(resolveValue(col, row, exportClassifierValues))).join(',')
      );
      const csvContent = [header, ...lines].join('\n');
      const date = new Date().toISOString().split('T')[0];
      triggerDownload(csvContent, `transactions_${case_id}_${date}.csv`);
    } finally {
      setExporting(false);
      setExportProgress(null);
      setClassifying(null);
      setClassifyProgress(null);
    }
  };

  return (
    <div className="mt-3">
      <h3 className="mb-3">Data Export</h3>

      <Card className="mb-3">
        <Card.Header>Filters</Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Label className="fw-bold">Source Type</Form.Label>
              {(Object.keys(SOURCE_LABELS) as SourceType[]).map(type => (
                <Form.Check
                  key={type}
                  type="checkbox"
                  id={`source-${type}`}
                  label={SOURCE_LABELS[type]}
                  checked={sourceTypes.has(type)}
                  onChange={() => toggleSourceType(type)}
                />
              ))}
            </Col>
            <Col md={4}>
              <Form.Label className="fw-bold">Date Range</Form.Label>
              <Form.Group className="mb-2">
                <Form.Label className="small text-muted mb-0">From</Form.Label>
                <Form.Control type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} size="sm" />
              </Form.Group>
              <Form.Group>
                <Form.Label className="small text-muted mb-0">To</Form.Label>
                <Form.Control type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} size="sm" />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-bold">Accounts</Form.Label>
              <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                {availableAccounts.length === 0 && <div className="text-muted small">No accounts found yet.</div>}
                {availableAccounts.map(acc => (
                  <Form.Check
                    key={acc.key}
                    type="checkbox"
                    id={`account-${acc.key}`}
                    label={acc.label}
                    checked={selectedAccounts.has(acc.key)}
                    onChange={() => toggleAccount(acc.key)}
                  />
                ))}
              </div>
              {selectedAccounts.size === 0 && (
                <small className="text-muted">No accounts selected = all accounts included.</small>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Columns</span>
          <Button variant="outline-primary" size="sm" onClick={() => setShowAddColumn(true)}>
            + Add Custom Column
          </Button>
        </Card.Header>
        <Card.Body>
          <small className="text-muted d-block mb-2">
            Drag <span aria-hidden>⠿</span> to reorder. Check to include in the preview/export.
            <span className="ms-2">
              <Badge bg="light" text="dark" className="border">Extracted</Badge> = came from file processing.
              {' '}<Badge bg="light" text="dark" className="border ms-1">Custom</Badge> = added by you.
            </span>
          </small>
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
              <ListGroup style={{ maxWidth: '600px' }}>
                {orderedColumns.map(col => (
                  <SortableColumnRow
                    key={col.id}
                    col={col}
                    checked={!!columnVisibility[col.id]}
                    onToggle={() => toggleColumnVisible(col.id)}
                    onRemove={() => removeColumn(col.id)}
                    onViewDefinition={col.definitionId ? () => setViewDefinitionId(col.definitionId!) : undefined}
                    classifying={col.definitionId != null && classifying === col.definitionId}
                    classifyProgress={classifyProgress}
                    onClassify={col.definitionId ? () => handleRunClassifierPreview(col.definitionId!) : undefined}
                  />
                ))}
              </ListGroup>
            </SortableContext>
          </DndContext>
          {activeClassifierColumns.length > 0 && (
            <small className="text-muted d-block mt-2">
              AI classifier columns are always fully classified at export time, even if you skip "Classify" here — this button just lets you preview results first.
            </small>
          )}
        </Card.Body>
      </Card>

      <ViewDefinitionModal
        definition={definitions.find(d => d.definition_id === viewDefinitionId) ?? null}
        onHide={() => setViewDefinitionId(null)}
        onDefinitionUpdated={(def) => {
          setDefinitions(prev => prev.map(d => d.definition_id === def.definition_id ? def : d).sort((a, b) => a.name.localeCompare(b.name)));
          setAllColumns(prev => prev.map(c => c.definitionId === def.definition_id ? { ...c, label: def.name } : c));
        }}
        token={token}
      />

      {error && <Alert variant="warning">{error}</Alert>}
      {glTruncated && (
        <Alert variant="info" className="py-2">
          Previewing the first 5,000 of {glTotalMatched.toLocaleString()} matching general ledger entries.
          The full set is still included when you export — this only limits the on-screen preview.
        </Alert>
      )}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            Preview
            {!loading && <Badge bg="secondary" className="ms-2">{rows.length.toLocaleString()} rows loaded</Badge>}
          </span>
          <div className="d-flex align-items-center gap-2">
            {exporting && (
              <small className="text-muted">
                {classifying
                  ? `Classifying ${classifyProgress ? `${classifyProgress.done}/${classifyProgress.total}` : '...'}`
                  : exportProgress
                  ? `Fetching ${exportProgress.loaded.toLocaleString()} of ${exportProgress.total.toLocaleString()}...`
                  : 'Preparing export...'}
              </small>
            )}
            <Button variant="primary" size="sm" onClick={handleExport} disabled={loading || exporting || rows.length === 0 || visibleOrderedColumns.length === 0}>
              {exporting ? <Spinner animation="border" size="sm" /> : 'Export CSV'}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center text-muted py-5">No transactions match the current filters.</div>
          ) : visibleOrderedColumns.length === 0 ? (
            <div className="text-center text-muted py-5">Select at least one column above to preview data.</div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '600px' }}>
              <Table striped bordered hover size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, background: 'var(--bs-body-bg)', zIndex: 1 }}>
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th key={header.id} style={{ whiteSpace: 'nowrap' }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.slice(0, 500).map(row => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
              {rows.length > 500 && (
                <div className="text-center text-muted small py-2">
                  Showing first 500 of {rows.length.toLocaleString()} rows in the preview — the full set is included in the export.
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <AddColumnModal
        show={showAddColumn}
        onHide={() => setShowAddColumn(false)}
        onAddAbsoluteValue={addAbsoluteValueColumn}
        definitions={definitions}
        onAddClassifier={addClassifierColumn}
        onDefinitionCreated={(def) => setDefinitions(prev => [...prev, def].sort((a, b) => a.name.localeCompare(b.name)))}
        onDefinitionDeleted={(id) => setDefinitions(prev => prev.filter(d => d.definition_id !== id))}
        token={token}
      />
    </div>
  );
};

// ── Sortable column row (dnd-kit) ───────────────────────────────────────────

interface SortableColumnRowProps {
  col: ExportColumn;
  checked: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onViewDefinition?: () => void;
  classifying: boolean;
  classifyProgress: { done: number; total: number } | null;
  onClassify?: () => void;
}

const SortableColumnRow: React.FC<SortableColumnRowProps> = ({
  col, checked, onToggle, onRemove, onViewDefinition, classifying, classifyProgress, onClassify,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: col.kind !== 'field' ? 'var(--bs-info-bg-subtle, #f0f8ff)' : undefined,
  };

  return (
    <ListGroup.Item ref={setNodeRef} style={style} className="d-flex align-items-center gap-2 py-1">
      <span
        {...attributes}
        {...listeners}
        className="text-muted"
        style={{ cursor: 'grab', touchAction: 'none' }}
        aria-label="Drag to reorder"
      >
        ⠿
      </span>
      <Form.Check
        type="checkbox"
        id={`colvis-${col.id}`}
        checked={checked}
        onChange={onToggle}
        className="mb-0"
      />
      <span className="flex-grow-1">{col.label}</span>
      {col.kind === 'field' ? (
        <Badge bg="light" text="dark" className="border">Extracted</Badge>
      ) : (
        <Badge bg="primary">Custom</Badge>
      )}
      {col.kind === 'absolute_value' && <Badge bg="secondary">Abs</Badge>}
      {col.kind === 'classifier' && <Badge bg="info">AI</Badge>}
      {onViewDefinition && (
        <Button variant="link" size="sm" className="p-0" onClick={onViewDefinition} title="View this classifier's definition">
          View
        </Button>
      )}
      {onClassify && (
        classifying ? (
          <small className="text-muted">
            {classifyProgress ? `${classifyProgress.done}/${classifyProgress.total}` : '...'}
          </small>
        ) : (
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={onClassify}
            title="Classify the currently loaded rows so the preview shows real values"
          >
            Classify
          </Button>
        )
      )}
      {col.removable && (
        <Button variant="link" size="sm" className="text-danger p-0" onClick={onRemove} title="Remove this custom column">
          ×
        </Button>
      )}
    </ListGroup.Item>
  );
};

// ── View / edit classifier definition modal ─────────────────────────────────

interface ViewDefinitionModalProps {
  definition: CustomColumnDefinition | null;
  onHide: () => void;
  onDefinitionUpdated: (definition: CustomColumnDefinition) => void;
  token?: string;
}

const ViewDefinitionModal: React.FC<ViewDefinitionModalProps> = ({ definition, onHide, onDefinitionUpdated, token }) => {
  const [editing, setEditing] = useState(false);

  // Reset to view mode whenever a different (or no) definition is shown.
  useEffect(() => { setEditing(false); }, [definition?.definition_id]);

  return (
    <Modal show={!!definition} onHide={onHide} size={editing ? 'lg' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? `Edit: ${definition?.name}` : definition?.name}</Modal.Title>
      </Modal.Header>
      {definition && (
        <Modal.Body>
          {editing ? (
            <ClassifierForm
              token={token}
              existing={definition}
              onSaved={(updated) => { onDefinitionUpdated(updated); setEditing(false); }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <>
              {definition.instructions && (
                <p className="text-muted">{definition.instructions}</p>
              )}
              <strong>Options</strong>
              <ListGroup className="mb-3 mt-1">
                {definition.choices.map(c => (
                  <ListGroup.Item key={c.label}>
                    <div className="fw-semibold">{c.label}</div>
                    {c.description && <small className="text-muted">{c.description}</small>}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <strong>Fields it looks at</strong>
              <div className="mt-1">
                {definition.context_columns.map(c => (
                  <Badge key={c} bg="light" text="dark" className="border me-1">
                    {CONTEXT_FIELD_OPTIONS.find(f => f.key === c)?.label ?? c}
                  </Badge>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
      )}
      {!editing && (
        <Modal.Footer>
          <Button variant="outline-primary" onClick={() => setEditing(true)}>Edit</Button>
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

// ── Add Custom Column modal ─────────────────────────────────────────────────

interface AddColumnModalProps {
  show: boolean;
  onHide: () => void;
  onAddAbsoluteValue: (sourceKey: keyof UnifiedRow, label: string) => void;
  definitions: CustomColumnDefinition[];
  onAddClassifier: (definition: CustomColumnDefinition) => void;
  onDefinitionCreated: (definition: CustomColumnDefinition) => void;
  onDefinitionDeleted: (definitionId: string) => void;
  token?: string;
}

const AddColumnModal: React.FC<AddColumnModalProps> = ({
  show, onHide, onAddAbsoluteValue, definitions, onAddClassifier, onDefinitionCreated, onDefinitionDeleted, token,
}) => {
  const [kind, setKind] = useState<'absolute_value' | 'classifier'>('absolute_value');
  const [sourceKey, setSourceKey] = useState<keyof UnifiedRow>('amount');
  const [classifierMode, setClassifierMode] = useState<'existing' | 'new'>(definitions.length > 0 ? 'existing' : 'new');
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<string>(definitions[0]?.definition_id ?? '');

  const handleAdd = () => {
    if (kind === 'absolute_value') {
      const fieldLabel = NUMERIC_FIELDS.find(f => f.key === sourceKey)?.label ?? String(sourceKey);
      onAddAbsoluteValue(sourceKey, `${fieldLabel} (Absolute Value)`);
    } else if (kind === 'classifier' && classifierMode === 'existing') {
      const def = definitions.find(d => d.definition_id === selectedDefinitionId);
      if (def) onAddClassifier(def);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size={kind === 'classifier' && classifierMode === 'new' ? 'lg' : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>Add Custom Column</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Column Type</Form.Label>
          <Form.Check
            type="radio"
            id="kind-absolute"
            name="column-kind"
            label="Absolute Value — always-positive version of a number column"
            checked={kind === 'absolute_value'}
            onChange={() => setKind('absolute_value')}
          />
          <Form.Check
            type="radio"
            id="kind-classifier"
            name="column-kind"
            label="AI Classifier — sorts each row into categories you define"
            checked={kind === 'classifier'}
            onChange={() => setKind('classifier')}
          />
        </Form.Group>

        {kind === 'absolute_value' && (
          <Form.Group>
            <Form.Label>Source Column</Form.Label>
            <Form.Select value={sourceKey as string} onChange={e => setSourceKey(e.target.value as keyof UnifiedRow)}>
              {NUMERIC_FIELDS.map(f => (
                <option key={f.key as string} value={f.key as string}>{f.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {kind === 'classifier' && (
          <>
            <div className="d-flex gap-3 mb-3">
              <Form.Check
                type="radio"
                id="clf-existing"
                name="classifier-mode"
                label="Use a saved classifier"
                checked={classifierMode === 'existing'}
                onChange={() => setClassifierMode('existing')}
                disabled={definitions.length === 0}
              />
              <Form.Check
                type="radio"
                id="clf-new"
                name="classifier-mode"
                label="Create a new classifier"
                checked={classifierMode === 'new'}
                onChange={() => setClassifierMode('new')}
              />
            </div>

            {classifierMode === 'existing' && (
              definitions.length === 0 ? (
                <Alert variant="light" className="border">No saved classifiers yet — create one instead.</Alert>
              ) : (
                <ListGroup>
                  {definitions.map(def => (
                    <ListGroup.Item
                      key={def.definition_id}
                      action
                      active={selectedDefinitionId === def.definition_id}
                      onClick={() => setSelectedDefinitionId(def.definition_id)}
                      className="d-flex justify-content-between align-items-start"
                    >
                      <div>
                        <div className="fw-semibold">{def.name}</div>
                        <small className="text-muted">
                          {def.choices.map(c => c.label).join(', ')}
                        </small>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCustomColumnDefinition(def.definition_id, token).then(() => onDefinitionDeleted(def.definition_id));
                        }}
                      >
                        Delete
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )
            )}

            {classifierMode === 'new' && (
              <ClassifierForm
                token={token}
                onSaved={(def) => {
                  onDefinitionCreated(def);
                  setSelectedDefinitionId(def.definition_id);
                  setClassifierMode('existing');
                }}
              />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={kind === 'classifier' && (classifierMode === 'new' || !selectedDefinitionId)}
        >
          Add Column
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Classifier definition builder (used for both create and edit) ──────────

interface ClassifierFormProps {
  token?: string;
  existing?: CustomColumnDefinition; // present => edit mode, absent => create mode
  onSaved: (definition: CustomColumnDefinition) => void;
  onCancel?: () => void;
}

const ClassifierForm: React.FC<ClassifierFormProps> = ({ token, existing, onSaved, onCancel }) => {
  const [name, setName] = useState(existing?.name ?? '');
  const [instructions, setInstructions] = useState(existing?.instructions ?? '');
  const [choices, setChoices] = useState<CustomColumnChoice[]>(
    existing?.choices && existing.choices.length > 0 ? existing.choices : [{ label: '', description: '' }, { label: '', description: '' }]
  );
  const [contextColumns, setContextColumns] = useState<Set<string>>(new Set(existing?.context_columns ?? ['description', 'amount']));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const updateChoice = (idx: number, field: 'label' | 'description', value: string) => {
    setChoices(prev => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };

  const addChoiceRow = () => setChoices(prev => [...prev, { label: '', description: '' }]);
  const removeChoiceRow = (idx: number) => setChoices(prev => prev.filter((_, i) => i !== idx));

  const toggleContextColumn = (key: string) => {
    setContextColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const validChoices = choices.filter(c => c.label.trim());
  const canSave = name.trim() && validChoices.length >= 2 && contextColumns.size > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: name.trim(),
        instructions: instructions.trim(),
        choices: validChoices,
        context_columns: Array.from(contextColumns),
      };
      const { definition } = existing
        ? await updateCustomColumnDefinition(existing.definition_id, payload, token)
        : await createCustomColumnDefinition({ ...payload, column_type: 'llm_classification' }, token);
      onSaved(definition);
    } catch (e: any) {
      setSaveError(e?.response?.data?.error ?? e?.message ?? 'Failed to save classifier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {existing && (
        <Alert variant="light" className="border py-2 small">
          Changes only affect future classifications — records already classified under this column keep their existing value unless you clear and re-run them.
        </Alert>
      )}
      <Form.Group className="mb-3">
        <Form.Label>Column Name</Form.Label>
        <Form.Control value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Type, Category" />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Instructions <span className="text-muted fw-normal">(optional context for the classifier)</span></Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={instructions}
          onChange={e => setInstructions(e.target.value)}
          placeholder="e.g. Classify each transaction by its role in personal cash flow."
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Options <span className="text-muted fw-normal">(at least 2 — each row must be mutually exclusive)</span></Form.Label>
        {choices.map((c, idx) => (
          <Row key={idx} className="mb-2 g-2">
            <Col md={3}>
              <Form.Control
                size="sm"
                placeholder="Label"
                value={c.label}
                onChange={e => updateChoice(idx, 'label', e.target.value)}
              />
            </Col>
            <Col md={8}>
              <Form.Control
                size="sm"
                placeholder="Description — what makes a row belong here"
                value={c.description}
                onChange={e => updateChoice(idx, 'description', e.target.value)}
              />
            </Col>
            <Col md={1} className="d-flex align-items-center">
              {choices.length > 2 && (
                <Button variant="link" size="sm" className="text-danger p-0" onClick={() => removeChoiceRow(idx)}>×</Button>
              )}
            </Col>
          </Row>
        ))}
        <Button variant="outline-secondary" size="sm" onClick={addChoiceRow}>+ Add Option</Button>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>What should it look at? <span className="text-muted fw-normal">(fields shown to the classifier per row)</span></Form.Label>
        <div className="d-flex flex-wrap gap-3">
          {CONTEXT_FIELD_OPTIONS.map(f => (
            <Form.Check
              key={f.key}
              type="checkbox"
              id={`ctx-${f.key}`}
              label={f.label}
              checked={contextColumns.has(f.key)}
              onChange={() => toggleContextColumn(f.key)}
            />
          ))}
        </div>
      </Form.Group>

      {saveError && <Alert variant="danger" className="py-2">{saveError}</Alert>}

      <div className="d-flex gap-2">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? <Spinner animation="border" size="sm" /> : existing ? 'Save Changes' : 'Save Classifier'}
        </Button>
        {onCancel && (
          <Button variant="outline-secondary" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
};

export default DataExport;
